//@flow
import React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { translate, type TFunction } from 'react-i18next';
import IconWithPopover from './IconWithPopover';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import { VisitStatusEnum } from 'common/enums/VisitEnums';
import { TabularTaskVisitTypeEnum } from 'common/enums/LiveViewEnums';
import EventComponentTypes from 'common/components/core/EventComponentTypes';
import theme from './VisitIcon.scss';
import type MinimizedMTourVisit from 'common/models/MinimizedMTourVisit';
import NumberUtils from 'common/utils/NumberUtils';
import DivWithPopover from 'common/components/core/wrappers/Div/DivWithPopover';

type Props = {
    isLatestOngoing?: boolean,
    showTooltipOnRight?: boolean,
    showAlerts?: boolean,
    showTooltip: boolean,
    visitType: string,
    theme: { +[string]: string },
    visit: MinimizedMTourVisit,
    t: TFunction
};

const iconMap = {
    [EventComponentTypes.SHIFT_START_VISIT]: 'circle',
    [EventComponentTypes.SHIFT_END_VISIT]: 'square',
    [EventComponentTypes.TASK_VISIT]: 'service-icon',
    [EventComponentTypes.PICKUP]: 'pickup-icon',
    [EventComponentTypes.DROP]: 'drop-icon',
    [EventComponentTypes.HOMEBASE]: 'home',
    [EventComponentTypes.BREAK]: 'pause',
    [EventComponentTypes.LUNCH]: 'coffee-cup',
    [EventComponentTypes.NIGHT]: 'night',
    [EventComponentTypes.REFUEL]: 'refuel',
    [TabularTaskVisitTypeEnum.Service]: 'service-icon'
};

const smallIcons = [EventComponentTypes.HOMEBASE, EventComponentTypes.LUNCH];

const VisitIcon = ({
    visit,
    isLatestOngoing,
    showTooltip,
    showTooltipOnRight,
    visitType,
    theme,
    t,
    showAlerts
}: Props) => {
    const { isSequenceEdited, slaBreached, taskSequence, visitStatus } = visit;
    const isHomebase = visitType === EventComponentTypes.HOMEBASE;
    const {
        color: visitColor,
        effectiveStatus,
        completedVisitsCount,
        cancelledVisitsCount
    } = visit.getColorAndStatus();
    let color = visitColor.name;
    if (Array.isArray(visitColor)) {
        color = visitColor.map(color => color.code);
    }
    let completedHomebaseVisitsCount;
    let cancelledHomebaseVisitsCount;
    // Mixed homebase status - Show counts separately in tooltip
    if (completedVisitsCount && cancelledVisitsCount) {
        completedHomebaseVisitsCount = t('completedVisitsCount', {
            count: completedVisitsCount
        });
        cancelledHomebaseVisitsCount = t('cancelledVisitsCount', {
            count: cancelledVisitsCount
        });
    }
    const tooltipText = (
        <>
            <div>{t(`visitTypes.${visitType.toLowerCase()}`)}</div>
            {effectiveStatus === VisitStatusEnum.Mixed ? (
                <>
                    <div>{completedHomebaseVisitsCount}</div>
                    <div>{cancelledHomebaseVisitsCount}</div>
                </>
            ) : (
                <div>{t(`${effectiveStatus.toLowerCase()}`)}</div>
            )}
        </>
    );
    const icon = (
        <div
            className={classnames(theme.visitIconWrap, {
                [theme.isLatestOngoing]: isLatestOngoing,
                [theme.isCollapsed]: showAlerts,
                [theme.iconWithNumber]:
                    showAlerts && NumberUtils.isNumeric(taskSequence)
            })}
        >
            <IconWithPopover
                className={classnames(theme.visitIcon, {
                    [theme.smallIcon]: smallIcons.includes(visitType),
                    [theme.squareIcon]: isHomebase,
                    [theme.waiting]:
                        effectiveStatus === VisitStatusEnum.Waiting,
                    [theme.isLatestOngoing]: isLatestOngoing
                })}
                theme={theme}
                icon={iconMap[visitType]}
                bgColor={color}
                triggerPopoverOn={showTooltip ? PopoverTriggerEnum.Hover : null}
                preferPlace={
                    showTooltipOnRight
                        ? PopoverPlaceEnum.Right
                        : PopoverPlaceEnum.Above
                }
                popOverContent={tooltipText}
            />
            {showAlerts && NumberUtils.isNumeric(taskSequence) ? (
                <DivWithPopover
                    className={classnames(theme.sequence, {
                        [theme.light]: visitStatus === VisitStatusEnum.Waiting
                    })}
                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                    popOverContent={t('taskSequence', {
                        taskSequence
                    })}
                >
                    {taskSequence}
                </DivWithPopover>
            ) : null}
        </div>
    );
    return (
        <div
            className={classnames(theme.visitIconAlertWrap, {
                [theme.showAlerts]:
                    showAlerts && (slaBreached || isSequenceEdited)
            })}
        >
            {isLatestOngoing ? (
                <div
                    className={classnames(theme.ripple, {
                        [theme.squareRipple]:
                            isHomebase ||
                            (showAlerts && NumberUtils.isNumeric(taskSequence))
                    })}
                >
                    {icon}
                </div>
            ) : (
                icon
            )}
            <div
                className={classnames(theme.alertsWrap, {
                    [theme.showAlerts]: showAlerts
                })}
            >
                {showAlerts && slaBreached ? (
                    <div
                        className={classnames(theme.alert, theme.slaBreached)}
                    />
                ) : null}
                {showAlerts && isSequenceEdited ? (
                    <div className={classnames(theme.alert)} />
                ) : null}
            </div>
        </div>
    );
};

VisitIcon.defaultProps = {
    showTooltip: true
};

export default themr(
    'ThemedVisitIcon',
    theme
)(translate('liveView')(VisitIcon));
